import { useCallback, useState } from 'react';

/**
 * A PER-BROWSER PREFERENCE (the scene list folded, highlights on or off, the reading size).
 *
 * Kept in localStorage because it is this person's habit at this desk, not the production's
 * state. Every read and write is guarded: a private window, blocked storage or a full quota
 * throws, and a preference that cannot be kept must never cost the screen.
 */
export function readPreference<T>(key: string, fallback: T, parse: (raw: string) => T | null): T {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return fallback;

    return parse(raw) ?? fallback;
  } catch {
    return fallback;
  }
}

export function writePreference(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Not kept: the preference lasts as long as the page.
  }
}

export function usePreference<T>(
  key: string,
  fallback: T,
  parse: (raw: string) => T | null,
  serialise: (value: T) => string = String,
): [T, (next: T | ((was: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => readPreference(key, fallback, parse));
  const set = useCallback(
    (next: T | ((was: T) => T)) => {
      setValue((was) => {
        const resolved = typeof next === 'function' ? (next as (was: T) => T)(was) : next;
        writePreference(key, serialise(resolved));

        return resolved;
      });
    },
    [key, serialise],
  );

  return [value, set];
}

export const parseBoolean = (raw: string): boolean | null => (raw === '1' ? true : raw === '0' ? false : null);
export const serialiseBoolean = (value: boolean): string => (value ? '1' : '0');

export const PREFERENCE_KEYS = {
  listCollapsed: 'sb.elements.listCollapsed',
  highlights: 'sb.elements.highlights',
  fontStep: 'sb.elements.fontStep',
  markKey: 'sb.elements.markKey',
} as const;
